import React from 'react';
import Button from './Button';
import { useImageGridReorder } from '../hooks/useImageGridReorder';

export interface ListingImagePreviewItem {
  id: string;
  src: string;
  name?: string;
}

export interface ListingImagePreviewGridProps {
  items: ListingImagePreviewItem[];
  onRemove: (id: string) => void;
  onReorder?: (fromIndex: number, toIndex: number) => void;
  title?: string;
}

/**
 * Thumbnail grid for selected listing images before upload/submit.
 * Supports drag-and-drop reordering on desktop and touch devices.
 */
const ListingImagePreviewGrid: React.FC<ListingImagePreviewGridProps> = ({
  items,
  onRemove,
  onReorder,
  title = 'Seçilen Fotoğraflar',
}) => {
  const { containerRef, getItemProps } = useImageGridReorder(onReorder);

  if (items.length === 0) return null;

  return (
    <div className="mb-3">
      <h6 className="fw-semibold mb-2">{title}</h6>
      {onReorder && items.length > 1 && (
        <p className="text-muted small mb-2">Sıralamak için görselleri sürükleyip bırakın.</p>
      )}
      <div className="d-flex flex-wrap gap-3" ref={containerRef}>
        {items.map((item, index) => {
          const itemProps = getItemProps(index);
          return (
            <div
              key={item.id}
              className="position-relative rounded overflow-hidden border bg-light"
              style={{ width: 120, height: 120, ...itemProps.style }}
              draggable={itemProps.draggable}
              onDragStart={itemProps.onDragStart}
              onDragOver={itemProps.onDragOver}
              onDrop={itemProps.onDrop}
              onDragEnd={itemProps.onDragEnd}
              onPointerDown={itemProps.onPointerDown}
              onPointerMove={itemProps.onPointerMove}
              onPointerUp={itemProps.onPointerUp}
              onPointerCancel={itemProps.onPointerCancel}
            >
              {index === 0 && (
                <span className="badge bg-primary position-absolute top-0 start-0 m-1" style={{ zIndex: 2 }}>
                  Kapak
                </span>
              )}
              <img
                src={item.src}
                alt={item.name ?? `Seçilen fotoğraf ${index + 1}`}
                className="w-100 h-100"
                style={{ objectFit: 'cover', pointerEvents: 'none' }}
                draggable={false}
              />
              <Button
                type="button"
                className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1 rounded-circle p-0 d-flex align-items-center justify-content-center"
                style={{ width: 28, height: 28, zIndex: 2 }}
                onClick={() => onRemove(item.id)}
                aria-label="Fotoğrafı kaldır"
              >
                <i className="fas fa-times" aria-hidden="true" />
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ListingImagePreviewGrid;
